import { useContext } from 'react';
import { IntercomContext } from './Intercom.context';
import { tIntercomContext } from '../../hooks/Intercom/Intercom.types';

/**
 * @module providers/IntercomProvider
 * Hook to access Intercom context from any component.
 *
 * @description This hook provides access to the Intercom context, ensuring that
 * the component is wrapped with IntercomProvider. It throws an error if used
 * outside of the provider.
 *
 * @returns {tIntercomContext} Intercom context containing state and methods
 * @throws {Error} When used outside of IntercomProvider
 *
 * @example
 * // In any component within IntercomProvider
 * function MyComponent() {
 *   const intercom = useIntercomContext();
 *
 *   const handleShowMessages = () => {
 *     intercom.methods.showMessages();
 *   };
 *
 *   return (
 *     <div>
 *       <p>Unread messages: {intercom.unreadCount}</p>
 *       <button onClick={handleShowMessages}>Show Messages</button>
 *     </div>
 *   );
 * }
 */
export function useIntercomContext(): tIntercomContext {
  const context = useContext(IntercomContext);

  if (!context) {
    throw new Error(
      'useIntercomContext must be used within an IntercomProvider',
    );
  }

  return context;
}
